import React, { useState, useEffect } from 'react';
import {
  X,
  ArrowRight,
  CheckCircle,
  Sparkles
} from 'lucide-react';

interface OnboardingWalkthroughProps {
  isOpen: boolean;
  onClose: () => void;
  onComplete: () => void;
  user: any;
}

interface WalkthroughStep {
  id: string;
  title: string;
  description: string;
  icon: React.ReactNode;
  image?: string;
  tips: string[];
  action?: {
    text: string;
    onClick: () => void;
  };
}

const OnboardingWalkthrough: React.FC<OnboardingWalkthroughProps> = ({
  isOpen,
  onClose,
  onComplete,
  user
}) => {
  const [currentStep] = useState(0);

  if (!isOpen) return null;

  const steps: WalkthroughStep[] = [
    {
      id: 'welcome',
      title: `Welcome to Piezit, ${user?.firstName || 'Friend'}!`,
      description: 'Discover amazing businesses and share your positive experiences. Click on map markers to explore, then share reviews to help others find great places!',
      icon: <Sparkles className="w-8 h-8 text-primary-600" />,
      tips: [
        'Click business markers on the map to see details',
        'Share positive reviews to help businesses thrive',
        'Use social sharing to spread the word about great places'
      ],
      action: {
        text: 'Get Started',
        onClick: () => {
          onComplete();
        }
      }
    }
  ];

  const currentStepData = steps[currentStep];


  const handleSkip = () => {
    localStorage.setItem('piezit-onboarding-completed', 'true');
    onComplete();
  };

  const handleComplete = () => {
    localStorage.setItem('piezit-onboarding-completed', 'true');
    if (currentStepData.action) {
      currentStepData.action.onClick();
    } else {
      onComplete();
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-primary-100 rounded-full flex items-center justify-center">
              {currentStepData.icon}
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Getting Started</h2>
              <p className="text-sm text-gray-500">
                Step {currentStep + 1} of {steps.length}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors duration-200"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Progress Bar */}
        <div className="px-6 py-2">
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div
              className="bg-primary-600 h-2 rounded-full transition-all duration-300"
              style={{ width: `${((currentStep + 1) / steps.length) * 100}%` }}
            />
          </div>
        </div>

        {/* Content */}
        <div className="p-6">
          <div className="text-center mb-6">
            <div className="w-16 h-16 bg-gradient-to-br from-primary-100 to-primary-200 rounded-full flex items-center justify-center mx-auto mb-4">
              {React.cloneElement(currentStepData.icon as React.ReactElement, {
                className: 'w-8 h-8 text-primary-600'
              })}
            </div>

            <h3 className="text-xl font-semibold text-gray-900 mb-2">
              {currentStepData.title}
            </h3>

            <p className="text-gray-600 max-w-lg mx-auto">
              {currentStepData.description}
            </p>
          </div>

          {/* Tips */}
          <div className="bg-gray-50 rounded-lg p-4 mb-6">
            <h4 className="font-medium text-gray-900 mb-3 flex items-center">
              <CheckCircle className="w-4 h-4 text-green-600 mr-2" />
              Key Features:
            </h4>
            <ul className="space-y-2">
              {currentStepData.tips.map((tip, index) => (
                <li key={index} className="flex items-start space-x-2 text-sm text-gray-700">
                  <div className="w-1.5 h-1.5 bg-primary-600 rounded-full mt-2 flex-shrink-0" />
                  <span>{tip}</span>
                </li>
              ))}
            </ul>
          </div>

        </div>

        {/* Navigation */}
        <div className="flex justify-between items-center p-6 border-t border-gray-200">
          <button
            onClick={handleSkip}
            className="text-gray-600 hover:text-gray-800 font-medium transition-colors duration-200"
          >
            Skip
          </button>

          <button
            onClick={handleComplete}
            className="flex items-center space-x-2 bg-primary-600 hover:bg-primary-700 text-white font-medium py-2 px-6 rounded-lg transition-colors duration-200"
          >
            <span>{currentStepData.action?.text || 'Get Started'}</span>
            <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default OnboardingWalkthrough;
